import { useMemo } from 'react'
import {
  parseISO,
  format,
  differenceInCalendarDays,
  startOfMonth,
  startOfWeek,
  addMonths,
  addWeeks,
  subDays,
  addDays,
  isBefore,
} from 'date-fns'
import { resolveAllDates } from '@/utils/calendarDateResolver'
import { cn } from '@/utils/cn'
import type { CalendarDetailResponse } from '@/types'

interface CalendarTimelineViewProps {
  calendar: CalendarDetailResponse
}

interface PositionedMilestone {
  id: string
  name: string
  date: Date
  dateStr: string
  dateMode: string
  pct: number
  row: number // 0-based vertical row within the lane (for stacking)
}

interface EventLane {
  eventId: string
  eventName: string
  milestones: PositionedMilestone[]
  maxRow: number // highest row index used (for sizing the lane)
}

function buildMonthMarkers(min: Date, max: Date, totalDays: number) {
  const markers: { label: string; pct: number }[] = []
  // Include the month that contains min
  let current = startOfMonth(min)
  if (isBefore(current, min)) current = startOfMonth(addMonths(min, 1))

  while (isBefore(current, max) || current.getTime() === max.getTime()) {
    const dayOffset = differenceInCalendarDays(current, min)
    const pct = (dayOffset / totalDays) * 100
    markers.push({ label: format(current, 'MMM yyyy'), pct })
    current = addMonths(current, 1)
  }

  return markers
}

function buildWeekLines(min: Date, max: Date, totalDays: number) {
  const lines: { pct: number }[] = []
  let current = startOfWeek(addWeeks(min, 1), { weekStartsOn: 1 })

  while (isBefore(current, max)) {
    const dayOffset = differenceInCalendarDays(current, min)
    const pct = (dayOffset / totalDays) * 100
    lines.push({ pct })
    current = addWeeks(current, 1)
  }

  return lines
}

/**
 * Group milestones that overlap (same pct within a tolerance) and assign
 * each a `row` index so they stack vertically instead of overlapping.
 * We use a simple greedy approach: for each milestone, check previously placed
 * milestones and pick the first row where there's no collision.
 */
function assignRows(milestones: Omit<PositionedMilestone, 'row'>[]): {
  milestones: PositionedMilestone[]
  maxRow: number
} {
  // Collision threshold: milestones within 4% of each other get stacked
  const COLLISION_PCT = 4
  const placed: PositionedMilestone[] = []
  let maxRow = 0

  for (const ms of milestones) {
    // Find which rows are occupied by nearby milestones
    const occupiedRows = new Set<number>()
    for (const p of placed) {
      if (Math.abs(p.pct - ms.pct) < COLLISION_PCT) {
        occupiedRows.add(p.row)
      }
    }
    // Find first available row
    let row = 0
    while (occupiedRows.has(row)) row++
    if (row > maxRow) maxRow = row

    placed.push({ ...ms, row })
  }

  return { milestones: placed, maxRow }
}

const PADDING_PCT = 5

export function CalendarTimelineView({ calendar }: CalendarTimelineViewProps) {
  const { lanes, minDate, maxDate, monthMarkers, weekLines } = useMemo(() => {
    const allMilestones = calendar.events.flatMap(e => e.milestones)
    const resolvedMap = resolveAllDates(allMilestones)

    const allDates: Date[] = []
    const milestoneDate = new Map<string, Date>()

    for (const [id, dateStr] of resolvedMap.entries()) {
      if (dateStr) {
        const d = parseISO(dateStr)
        allDates.push(d)
        milestoneDate.set(id, d)
      }
    }

    if (allDates.length === 0) {
      return { lanes: [], minDate: null, maxDate: null, monthMarkers: [] }
    }

    allDates.sort((a, b) => a.getTime() - b.getTime())
    const min = subDays(allDates[0], 7)
    const max = addDays(allDates[allDates.length - 1], 7)
    const totalDays = differenceInCalendarDays(max, min) || 1
    const usableRange = 100 - PADDING_PCT * 2

    const eventLanes: EventLane[] = calendar.events
      .filter(e => e.milestones.some(m => milestoneDate.has(m.id)))
      .map(event => {
        const rawPositioned = event.milestones
          .filter(m => milestoneDate.has(m.id))
          .map(m => {
            const d = milestoneDate.get(m.id)!
            const dayOffset = differenceInCalendarDays(d, min)
            const rawPct = dayOffset / totalDays
            return {
              id: m.id,
              name: m.name,
              date: d,
              dateStr: format(d, 'MMM d'),
              dateMode: m.date_mode,
              pct: PADDING_PCT + rawPct * usableRange,
            }
          })
          .sort((a, b) => a.date.getTime() - b.date.getTime())

        const { milestones: positioned, maxRow } = assignRows(rawPositioned)

        return {
          eventId: event.id,
          eventName: event.name,
          milestones: positioned,
          maxRow,
        }
      })

    const markers = buildMonthMarkers(min, max, totalDays).map(m => ({
      ...m,
      pct: PADDING_PCT + (m.pct / 100) * usableRange,
    }))

    const weeks = buildWeekLines(min, max, totalDays).map(w => ({
      pct: PADDING_PCT + (w.pct / 100) * usableRange,
    }))

    return {
      lanes: eventLanes,
      minDate: allDates[0],
      maxDate: allDates[allDates.length - 1],
      monthMarkers: markers,
      weekLines: weeks,
    }
  }, [calendar])

  if (!minDate || !maxDate || lanes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <p className="text-sm">No milestones with dates to display.</p>
      </div>
    )
  }

  // Each stacked row adds this much height
  const ROW_HEIGHT = 52
  const BASE_LANE_HEIGHT = 70
  const DOT_SIZE = 12 // h-3 w-3
  const DOT_CENTER = DOT_SIZE / 2
  const DOT_TOP_BASE = 12

  return (
    <div>
      {/* Wrapper: axis + lanes share the same coordinate space for gridlines */}
      <div className="relative">
        {/* Full-height week gridlines (behind everything) */}
        {weekLines.map((w, i) => (
          <div
            key={`w-${i}`}
            className="pointer-events-none absolute top-0 h-full w-px bg-border/25"
            style={{ left: `${w.pct}%` }}
          />
        ))}
        {/* Full-height month gridlines (stronger, behind everything) */}
        {monthMarkers.map((m, i) => (
          <div
            key={`m-${i}`}
            className="pointer-events-none absolute top-0 h-full w-px bg-border/60"
            style={{ left: `${m.pct}%` }}
          />
        ))}

        {/* Date axis — month labels */}
        <div className="relative h-6 border-b">
          {monthMarkers.map((marker, idx) => (
            <div
              key={idx}
              className="absolute bottom-0 flex flex-col items-center"
              style={{ left: `${marker.pct}%`, transform: 'translateX(-50%)' }}
            >
              <span className="bg-background px-1 text-xs font-medium text-muted-foreground">
                {marker.label}
              </span>
            </div>
          ))}
        </div>

        {/* Swim lanes */}
        <div className="mt-2 space-y-6">
          {lanes.map(lane => {
            const laneHeight = BASE_LANE_HEIGHT + lane.maxRow * ROW_HEIGHT
            const lineTop = DOT_TOP_BASE + DOT_CENTER

            return (
              <div key={lane.eventId}>
                <p className="mb-1 text-sm font-medium">{lane.eventName}</p>
                <div
                  className="relative overflow-hidden rounded-md bg-muted/30"
                  style={{ height: laneHeight }}
                >
                  {/* Connecting line — always at row-0 level */}
                  {lane.milestones.length > 1 && (
                    <div
                      className="absolute h-[2px] bg-border"
                      style={{
                        top: lineTop,
                        left: `${lane.milestones[0].pct}%`,
                        width: `${lane.milestones[lane.milestones.length - 1].pct - lane.milestones[0].pct}%`,
                      }}
                    />
                  )}

                  {/* Milestone dots + labels */}
                  {lane.milestones.map(ms => {
                    const dotTop = DOT_TOP_BASE + ms.row * ROW_HEIGHT

                    return (
                      <div
                        key={ms.id}
                        className="absolute flex flex-col items-center"
                        style={{
                          left: `${ms.pct}%`,
                          transform: 'translateX(-50%)',
                          top: dotTop,
                        }}
                      >
                        {/* Vertical connector line from row-0 to this row */}
                        {ms.row > 0 && (
                          <div
                            className="absolute w-[2px] bg-border"
                            style={{
                              top: -(ms.row * ROW_HEIGHT) + DOT_CENTER,
                              height: ms.row * ROW_HEIGHT,
                              left: '50%',
                              transform: 'translateX(-50%)',
                            }}
                          />
                        )}
                        {/* Dot */}
                        <div
                          className={cn(
                            'relative z-10 h-3 w-3 shrink-0 rounded-full',
                            ms.dateMode === 'SPECIFIC'
                              ? 'bg-primary'
                              : 'border-2 border-primary bg-background'
                          )}
                          title={`${ms.name}: ${ms.dateStr}`}
                        />
                        {/* Name */}
                        <span className="mt-1.5 max-w-[80px] truncate text-center text-[10px] leading-tight text-muted-foreground">
                          {ms.name}
                        </span>
                        {/* Date */}
                        <span className="text-[10px] leading-tight font-medium">
                          {ms.dateStr}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 pt-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-primary" />
          <span>Specific date</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full border-2 border-primary bg-background" />
          <span>Calculated date</span>
        </div>
      </div>
    </div>
  )
}
